// pages/Home.jsx
import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import ServicesSection from "../features/services/ServicesSection";
import BeforeAfterGallery from "../features/gallery/BeforeAfterGallery";
import useSeo from "../hooks/useSeo";

export default function Home() {
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  useSeo({
    title: "Thermo Fix | სანტექნიკის და გათბობის სერვისი თბილისში",
    description:
      "Thermo Fix გთავაზობთ სანტექნიკის პროფესიონალურ მომსახურებას: გათბობის მონტაჟი, ქვაბის დიაგნოსტიკა, ავარიული გამოძახება და რემონტი 24/7.",
    path: "/",
    keywords:
      "სანტექნიკოსი თბილისი, plumber tbilisi, გათბობის მონტაჟი, ქვაბის რემონტი, boiler service georgia",
    image: "/boiler.png",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Plumber",
      name: "Thermo Fix",
      url: "https://thermofix.com.ge/",
      telephone: "+995557141614",
      image: "https://thermofix.com.ge/boiler.png",
      areaServed: "Tbilisi, Georgia",
      sameAs: ["https://www.facebook.com/Tbil.Heating/"],
      serviceType: [
        "Plumbing Services",
        "Boiler Diagnostics",
        "Heating Installation",
      ],
    },
  });

  const handleParallaxMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width - 0.5;
    const py = (event.clientY - rect.top) / rect.height - 0.5;
    setParallax({ x: px, y: py });
  };

  const resetParallax = () => setParallax({ x: 0, y: 0 });

  return (
    <div id="top" className="relative overflow-hidden">
      <div className="pointer-events-none absolute -top-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-[#b2d3ff]/40 blur-3xl dark:bg-[#1f3558]/50" />
      <div className="pointer-events-none absolute right-0 top-72 h-64 w-64 rounded-full bg-[#d0e4ff]/50 blur-3xl dark:bg-[#1a2d4b]/55" />

      <Header />

      {/* HERO */}
      <section className="relative mx-auto min-h-[78vh] w-full max-w-6xl px-6 py-16">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <div className="glass-panel animate-rise p-8 md:p-6">
              <p className="inline-flex rounded-full border border-[#dbe8fa] bg-white px-4 py-4 text-xs font-semibold uppercase tracking-[0.16em] text-[#527db6] dark:border-[#3b5476] dark:bg-[#17263a] dark:text-[#95b5e6]">
                24/7 დაგვიკავშირდით ნებისმიერ დროს
              </p>
              <span className="rounded-full  px-4 py-4 text-3xl font-semibold text-[#0f3d7a] dark:bg-[#17263a] dark:text-[#c9dcf8] md:inline-flex md:text-4xl">
                +995 557 14 16 14
              </span>
              <h2 className="mt-6 max-w-2xl text-[2.1rem] font-bold leading-[1.08] tracking-[-0.03em] text-[#0d2340] dark:text-[#e8f1ff] sm:text-[2.6rem] md:text-[3.35rem]">
                <span className="block">სანტექნიკის</span>
                <span className="block text-[#123d74] dark:text-[#9ac0f4]">სერვისი თქვენი</span>
                <span className="block">სახლის სიმშვიდისთვის</span>
              </h2>

              <p className="mt-5 max-w-2xl text-lg leading-8 text-[#4e6485] dark:text-[#c1d4f0]">
                სწრაფად, ხარისხიანად და სუფთად შესრულებული სამუშაო,
                გამჭვირვალე ფასით და პასუხისმგებლიანი გუნდის მხარდაჭერით.
              </p>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row">                
                 <a
                  href="tel:+995555123456"
                  className="group relative overflow-hidden rounded-2xl bg-[#0f3d7a] px-7 py-3 text-sm font-semibold tracking-wide text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#1452a3] hover:shadow-[0_14px_34px_rgba(15,61,122,0.35)] dark:bg-[#284c7f] dark:hover:bg-[#3561a0]"
                >
                  📞  უფასო კონსულტაცია
                </a>
                <Link
                  to="/services"
                  className="rounded-2xl border border-[#d4e2f5] px-7 py-3 text-sm font-semibold text-[#24466f] transition hover:-translate-y-0.5 hover:border-[#b7cff0] hover:bg-white/80 dark:border-[#365073] dark:text-[#c9dcf8] dark:hover:bg-[#182b44]"
                >
                  სერვისების ნახვა
                </Link>
                <a
                  href="https://www.facebook.com/Tbil.Heating/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-2xl bg-[#1877F2] px-6 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:bg-[#1266d1]"
                >
                <span className="text-lg font-black leading-none">f</span>
                  Facebook გვერდი
                </a>
              </div>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              {[
                { label: "რეაგირება", value: "30 წთ" },
                { label: "კლიენტის კმაყოფილება", value: "98%" },
                { label: "გარანტია", value: "12 თვე" },
              ].map((item, idx) => (
                <div
                  key={item.label}
                  className="glass-panel animate-rise rounded-2xl p-4"
                  style={{ animationDelay: `${idx * 140}ms` }}
                >
                  <p className="text-2xl font-bold text-[#102947] dark:text-[#eaf2ff]">{item.value}</p>
                  <p className="mt-1 text-sm text-[#527095] dark:text-[#aec6e9]">{item.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div
            className="relative w-full animate-rise lg:h-full"
            onMouseMove={handleParallaxMove}
            onMouseLeave={resetParallax}
          >
            <div className="absolute -left-6 top-8 h-40 w-40 rounded-full bg-[#b8d6ff]/55 blur-3xl dark:bg-[#1f3558]/55" />
            <div className="absolute -right-8 bottom-4 h-44 w-44 rounded-full bg-[#cfe4ff]/60 blur-3xl dark:bg-[#20314f]/60" />

            <div
              className="relative z-10 h-full overflow-hidden rounded-[28px] border border-white/70 bg-white/40 p-3 shadow-[0_28px_70px_rgba(15,35,64,0.2)] backdrop-blur-sm transition-transform duration-300 dark:border-[#385172] dark:bg-[#15263d]/65"
              style={{
                transform: `translate3d(${parallax.x * 8}px, ${parallax.y * 8}px, 0)`,
              }}
            >
              <img
                src="/boiler.jpg"
                alt="ცენტრალური გათბობის ბოილერის სერვისი"
                className="h-[360px] w-full rounded-2xl object-cover sm:h-[440px] lg:h-full lg:min-h-[620px]"
                loading="eager"
              />
            </div>

            <div
              className="absolute -left-5 top-10 z-20 rounded-2xl bg-white/95 px-4 py-3 shadow-xl transition-transform duration-300 dark:bg-[#16273f]/95"
              style={{
                transform: `translate3d(${parallax.x * -10}px, ${parallax.y * -10}px, 0)`,
              }}
            >
              <p className="text-xs uppercase tracking-[0.14em] text-[#5a7fb3] dark:text-[#93b5e6]">
                თქვენ გვერდით
              </p>
              <p className="mt-1 text-lg font-bold text-[#173a67] dark:text-[#e1ecff]">24/7 დახმარება</p>
            </div>

            <div
              className="absolute -right-5 bottom-12 z-20 rounded-2xl bg-white/95 px-4 py-3 shadow-xl transition-transform duration-300 dark:bg-[#16273f]/95"
              style={{
                transform: `translate3d(${parallax.x * -12}px, ${parallax.y * -12}px, 0)`,
              }}
            >
              <p className="text-xs uppercase tracking-[0.14em] text-[#5a7fb3] dark:text-[#93b5e6]">
                გამოცდილება
              </p>
              <p className="mt-1 text-lg font-bold text-[#173a67] dark:text-[#e1ecff]">10+ წელი</p>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <ServicesSection previewCount={3} showViewAll />

      {/* BEFORE / AFTER GALLERY */}
      <BeforeAfterGallery />

      {/* CONTACT */}
      <section id="contact" className="mx-auto w-full max-w-6xl px-6 py-14">
        <div className="animate-rise rounded-3xl border border-[#d7e5f9] bg-gradient-to-r from-[#0f3d7a] to-[#205a9f] p-8 text-white shadow-[0_24px_60px_rgba(13,35,64,0.28)] dark:border-[#2f4668] dark:from-[#1c3252] dark:to-[#244266] md:flex md:items-center md:justify-between md:p-12">
          <div>
            <h2 className="text-3xl font-bold md:text-4xl">
              დაგვიკავშირდი დღესვე
            </h2>
            <p className="mt-4 max-w-2xl text-white/85">
              დაგვირეკე ან მოგვწერე, მოვისმენთ პრობლემას და შემოგთავაზებთ
              საუკეთესო გადაწყვეტილებას.
            </p>
          </div>
          <div className="mt-6 flex flex-col items-start gap-3 md:mt-0 md:items-end">
            <a
              href="tel:+995555123456"
              className="inline-flex rounded-2xl bg-white px-7 py-3 text-sm font-semibold text-[#0f3d7a] transition hover:-translate-y-0.5 hover:bg-[#eef5ff] dark:bg-[#1a2e49] dark:text-[#d5e6ff] dark:hover:bg-[#223a5a]"
            >
              📞 დარეკვა ახლა
            </a>
            <a
              href="https://www.facebook.com/Tbil.Heating/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-2xl bg-[#1877F2] px-6 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:bg-[#1266d1]"
            >
              <span className="text-lg font-black leading-none">f</span>
              Facebook გვერდი
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
